/**
 * CRUD de tenants para o painel de superadmin.
 *
 * RLS já permite operações for-all quando is_superadmin() == true
 * (ver migration 20260520000600_superadmin.sql).
 *
 * Endpoints lógicos:
 *  - useAllTenants()             → lista todos os tenants + branding + member_count
 *  - useTenantDetail(id)         → carrega 1 tenant com branding completo
 *  - useCreateTenant()           → cria tenant + branding inicial (transação client-side)
 *  - useUpdateTenant()           → atualiza colunas do tenant
 *  - useUpdateTenantBranding()   → atualiza colunas do tenant_branding
 *  - useToggleTenantActive()     → liga/desliga sem reabrir o modal
 *  - useDeleteTenant()           → hard delete (cascade nas dependências)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// ── Tipos ────────────────────────────────────────────────────────────────────

export type PlanName = 'essencial' | 'pro' | 'enterprise';

export interface TenantBranding {
  tenant_id:       string;
  logo_url:        string | null;
  primary_color:   string;
  accent_color:    string;
  candidate_name:  string | null;
  party:           string | null;
  slogan:          string | null;
  app_name:        string;
  uf?:             string | null;
  municipio?:      string | null;
  updated_at?:     string;
}

export interface TenantRow {
  id:                       string;
  slug:                     string;
  plan:                     PlanName;
  active:                   boolean;
  max_external_dispatches:  number;
  timezone:                 string;
  created_at:               string;
  updated_at?:              string;
  tenant_branding:          TenantBranding | null;
  member_count?:            number;
}

// ── List ─────────────────────────────────────────────────────────────────────

export function useAllTenants() {
  return useQuery<TenantRow[]>({
    queryKey: ['superadmin', 'all-tenants'],
    queryFn: async (): Promise<TenantRow[]> => {
      const { data, error } = await db
        .from('tenants')
        .select(`
          id, slug, plan, active, max_external_dispatches, timezone, created_at, updated_at,
          tenant_branding(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const rows = (data ?? []) as Array<Record<string, unknown>>;

      // Conta membros por tenant — uma query única
      const tenantIds = rows.map((r) => r.id as string);
      let countsByTenant: Record<string, number> = {};
      if (tenantIds.length > 0) {
        const { data: members } = await db
          .from('tenant_members')
          .select('tenant_id')
          .in('tenant_id', tenantIds);
        countsByTenant = ((members ?? []) as { tenant_id: string }[])
          .reduce((acc, m) => {
            acc[m.tenant_id] = (acc[m.tenant_id] ?? 0) + 1;
            return acc;
          }, {} as Record<string, number>);
      }

      return rows.map((r) => ({
        id:                      r.id as string,
        slug:                    r.slug as string,
        plan:                    r.plan as PlanName,
        active:                  r.active as boolean,
        max_external_dispatches: (r.max_external_dispatches as number) ?? 0,
        timezone:                (r.timezone as string) ?? 'America/Sao_Paulo',
        created_at:              r.created_at as string,
        updated_at:              r.updated_at as string,
        tenant_branding:         (r.tenant_branding as TenantBranding | null) ?? null,
        member_count:            countsByTenant[r.id as string] ?? 0,
      }));
    },
    staleTime: 30_000,
  });
}

// ── Detail ───────────────────────────────────────────────────────────────────

export function useTenantDetail(tenantId: string | null) {
  return useQuery<TenantRow | null>({
    queryKey: ['superadmin', 'tenant', tenantId],
    queryFn: async (): Promise<TenantRow | null> => {
      if (!tenantId) return null;
      const { data, error } = await db
        .from('tenants')
        .select(`
          id, slug, plan, active, max_external_dispatches, timezone, created_at, updated_at,
          tenant_branding(*)
        `)
        .eq('id', tenantId)
        .single();
      if (error) throw error;
      return data as TenantRow;
    },
    enabled: !!tenantId,
  });
}

// ── Create ───────────────────────────────────────────────────────────────────

export interface CreateTenantInput {
  // tenants
  slug:                     string;
  plan:                     PlanName;
  active?:                  boolean;
  max_external_dispatches?: number;
  // tenant_branding (opcional — usa defaults se omitido)
  app_name?:                string;
  candidate_name?:          string | null;
  party?:                   string | null;
  slogan?:                  string | null;
  primary_color?:           string;
  accent_color?:            string;
  logo_url?:                string | null;
}

export function useCreateTenant() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateTenantInput): Promise<TenantRow> => {
      const slug = normalizeSlug(input.slug);
      if (!slug) throw new Error('Slug inválido.');

      // 1. Cria o tenant
      const { data: tenant, error: tErr } = await db
        .from('tenants')
        .insert({
          slug,
          plan:                    input.plan,
          active:                  input.active ?? true,
          max_external_dispatches: input.max_external_dispatches ?? 50,
        })
        .select('*')
        .single();

      if (tErr || !tenant) {
        if (tErr?.code === '23505') {
          throw new Error(`Slug "${slug}" já está em uso.`);
        }
        throw new Error(tErr?.message ?? 'Falha ao criar tenant.');
      }

      // 2. Cria o branding inicial (mesmo que default)
      const { error: bErr } = await db
        .from('tenant_branding')
        .insert({
          tenant_id:      tenant.id,
          app_name:       input.app_name       ?? 'Lumenise Base',
          candidate_name: input.candidate_name ?? null,
          party:          input.party          ?? null,
          slogan:         input.slogan         ?? null,
          primary_color:  input.primary_color  ?? '#0A2342',
          accent_color:   input.accent_color   ?? '#00C2FF',
          logo_url:       input.logo_url       ?? null,
        });

      if (bErr) {
        // Rollback manual: remove o tenant se branding falhar
        await db.from('tenants').delete().eq('id', tenant.id);
        throw new Error(`Tenant criado mas branding falhou: ${bErr.message}`);
      }

      return tenant as TenantRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'all-tenants'] });
    },
  });
}

// ── Update tenant ────────────────────────────────────────────────────────────

export interface UpdateTenantInput {
  id:                       string;
  slug?:                    string;
  plan?:                    PlanName;
  active?:                  boolean;
  max_external_dispatches?: number;
  timezone?:                string;
}

export function useUpdateTenant() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, slug, ...rest }: UpdateTenantInput) => {
      const payload: Record<string, unknown> = { ...rest };
      if (slug !== undefined) payload.slug = normalizeSlug(slug);

      const { data, error } = await db
        .from('tenants')
        .update(payload)
        .eq('id', id)
        .select('*')
        .single();

      if (error) {
        if (error.code === '23505') throw new Error(`Slug já está em uso.`);
        throw error;
      }
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'all-tenants'] });
      qc.invalidateQueries({ queryKey: ['superadmin', 'tenant', vars.id] });
    },
  });
}

// ── Update tenant branding ───────────────────────────────────────────────────

export interface UpdateBrandingInput {
  tenant_id:       string;
  app_name?:       string;
  candidate_name?: string | null;
  party?:          string | null;
  slogan?:         string | null;
  primary_color?:  string;
  accent_color?:   string;
  logo_url?:       string | null;
  uf?:             string | null;
  municipio?:      string | null;
}

export function useUpdateTenantBranding() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenant_id, ...rest }: UpdateBrandingInput) => {
      // Upsert (tenant_branding tem PK em tenant_id)
      const { data, error } = await db
        .from('tenant_branding')
        .upsert({ tenant_id, ...rest }, { onConflict: 'tenant_id' })
        .select('*')
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'all-tenants'] });
      qc.invalidateQueries({ queryKey: ['superadmin', 'tenant', vars.tenant_id] });
    },
  });
}

// ── Toggle ativo (atalho) ────────────────────────────────────────────────────

export function useToggleTenantActive() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await db.from('tenants').update({ active }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'all-tenants'] });
    },
  });
}

// ── Delete ───────────────────────────────────────────────────────────────────

export function useDeleteTenant() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (tenantId: string) => {
      // ON DELETE CASCADE remove branding/members/etc automaticamente
      const { error } = await db.from('tenants').delete().eq('id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'all-tenants'] });
    },
  });
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function normalizeSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}
